import React, { useState, useEffect, useReducer } from "react"
import { connect } from "react-redux"
import {
  Row,
  Col,
  Form,
  Select,
  Input,
  Button,
  Table,
  Tag,
  Space,
  Alert,
  Spin,
  Image,
  Tooltip,
  Card,
  Typography,
} from "antd"
import {
  UploadOutlined,
  SaveOutlined,
  DeleteOutlined,
  DownloadOutlined,
  FileImageOutlined,
} from "@ant-design/icons"
import { withTranslation } from "react-i18next"
import {
  addUserFile,
  deleteUserFile,
  getUserFiles,
  resetUSERFlag,
} from "store/actions"
import { getCurrentUser, enumToArray } from "helpers/service_helper"
import { UserFileType } from "models/types/user-file-type"
import FilePondUpload from "components/Common/FilePondUpload"
import { getApiUrlUserFiles } from "common/global"

const { Text } = Typography

const UserFiles = props => {
  const { lstUserFiles } = props
  const [files, setFiles] = useState([])
  const [form] = Form.useForm()
  const [formInput, setFormInput] = useReducer(
    (state, newState) => ({ ...state, ...newState }),
    {
      fileTypeEnum: 2,
      title: props.t("enums." + UserFileType[2]),
    }
  )
  const [userFileTypes, setUserFileTypes] = useState([])
  const [errorMessage, setErrorMessage] = useState(null)

  useEffect(() => {
    setUserFileTypes(enumToArray(UserFileType))
  }, [])

  useEffect(() => {
    props.onGetUserFiles()
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => {
      setErrorMessage(null)
      props.onResetUSERFlag()
    }, 3000)
    return () => clearTimeout(timer)
  }, [errorMessage, props.success, props.error])

  const saveToDatabase = async values => {
    const { onAddUserFile } = props
    const file = files[0].file
    const fileName = UserFileType[values.fileTypeEnum]
    const formData = new FormData()
    formData.append(
      "file",
      file,
      fileName +
        "-" +
        file.name.substr(0, file.name.lastIndexOf(".")) +
        "." +
        file.name.split(".")[file.name.split(".").length - 1]
    )
    formData.append("name", fileName)
    formData.append("title", values.title)
    formData.append("des", `Upload with-${getCurrentUser().sid}`)
    formData.append("userFileTypeEnum", fileName)
    formData.append("FolderName", "UserFiles")
    formData.append(
      "FileName",
      fileName +
        "." +
        file.name.split(".")[file.name.split(".").length - 1]
    )
    formData.append("userId", getCurrentUser().sid)
    formData.append("toUserId", getCurrentUser().sid)
    await onAddUserFile(formData)
  }

  const handleFinish = async values => {
    if (files[0]) {
      await saveToDatabase({ ...formInput, ...values })
    } else {
      setErrorMessage("برای ذخیره ابتدا فایل را بارگذاری کنید")
    }
  }

  const handleFileTypeChange = value => {
    setFormInput({ fileTypeEnum: value, title: props.t("enums." + UserFileType[value]) })
    form.setFieldsValue({ title: props.t("enums." + UserFileType[value]) })
  }

  const availableTypes = (userFileTypes || []).filter(
    row =>
      row.id !== 0 &&
      lstUserFiles &&
      !lstUserFiles.find(x => x.fileTypeName === row.name)
  )

  const columns = [
    {
      title: "نوع فایل",
      dataIndex: "fileTypeName",
      key: "fileTypeName",
      render: name => (
        <Tag color="blue" icon={<FileImageOutlined />}>
          {props.t("enums." + name)}
        </Tag>
      ),
    },
    {
      title: "موضوع فایل",
      dataIndex: "title",
      key: "title",
    },
    {
      title: "پیش‌نمایش",
      key: "preview",
      render: (_, row) => (
        <Image
          height={60}
          width={60}
          style={{ borderRadius: 6, objectFit: "cover" }}
          src={getApiUrlUserFiles(row.folderName + "/" + row.fileName)}
          alt="file"
        />
      ),
    },
    {
      title: "دانلود",
      key: "download",
      render: (_, row) => (
        <Tooltip title="دانلود فایل">
          <Button
            type="primary"
            ghost
            icon={<DownloadOutlined />}
            href={getApiUrlUserFiles(row.folderName + "/" + row.fileName)}
            download
            size="small"
            style={{ borderRadius: 6 }}
          >
            دانلود
          </Button>
        </Tooltip>
      ),
    },
  ]

  return (
    <Card
      bordered={false}
      style={{ borderRadius: 16, boxShadow: "0 2px 12px rgba(0,0,0,0.08)" }}
      title={
        <Space>
          <UploadOutlined style={{ color: "#1890ff" }} />
          <span>مدارک و فایل‌های من</span>
        </Space>
      }
    >
      {errorMessage && (
        <Alert
          message={errorMessage}
          type="error"
          showIcon
          closable
          style={{ marginBottom: 16, borderRadius: 8 }}
          onClose={() => setErrorMessage(null)}
        />
      )}
      {props.success && (
        <Alert
          message="فایل با موفقیت ذخیره شد"
          type="success"
          showIcon
          style={{ marginBottom: 16, borderRadius: 8 }}
        />
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        initialValues={{ fileTypeEnum: 2, title: props.t("enums." + UserFileType[2]) }}
      >
        <Row gutter={[16, 0]}>
          <Col xs={24} sm={8}>
            <Form.Item
              name="fileTypeEnum"
              label="نوع فایل"
              rules={[{ required: true, message: "نوع فایل را انتخاب کنید" }]}
            >
              <Select
                onChange={handleFileTypeChange}
                style={{ borderRadius: 8 }}
                placeholder="انتخاب نوع فایل"
              >
                {availableTypes.map(row => (
                  <Select.Option key={row.id} value={row.id}>
                    {props.t("enums." + row.name)}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={16}>
            <Form.Item
              name="title"
              label="موضوع فایل"
              rules={[{ required: true, message: "موضوع فایل الزامی است" }]}
            >
              <Input
                placeholder="موضوع فایل را وارد کنید"
                style={{ borderRadius: 8 }}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item label={`${props.t("enums." + UserFileType[formInput.fileTypeEnum])} را اینجا بکشید`}>
          <FilePondUpload
            setFiles={setFiles}
            maxFileSize="800KB"
            allowMultiple={false}
            maxFiles={1}
            acceptedFileTypes={["image/png", "image/jpeg"]}
            imagePreviewMaxHeight={600}
            labelText={`${props.t("enums." + UserFileType[formInput.fileTypeEnum])} را اینجا بکشید`}
            waterMark={false}
          />
        </Form.Item>

        <Form.Item>
          {props.loading ? (
            <Spin />
          ) : (
            <Button
              type="primary"
              htmlType="submit"
              icon={<SaveOutlined />}
              size="large"
              style={{ borderRadius: 8, minWidth: 140 }}
            >
              ذخیره فایل
            </Button>
          )}
        </Form.Item>
      </Form>

      <Table
        rowKey="id"
        dataSource={lstUserFiles}
        columns={columns}
        pagination={{ pageSize: 8, showSizeChanger: true, pageSizeOptions: ["8", "12", "50", "100"] }}
        bordered={false}
        scroll={{ x: true }}
        rowClassName={(_, index) => (index % 2 === 0 ? "" : "ant-table-row-alt")}
        style={{ marginTop: 8 }}
      />
    </Card>
  )
}

const mapStateToProps = ({ USERs }) => ({
  lstUserFiles: USERs.lstUserFiles,
  error: USERs.error,
  success: USERs.success,
  loading: USERs.loading,
})

const mapDispatchToProps = dispatch => ({
  onGetUserFiles: searchValue => dispatch(getUserFiles(searchValue)),
  onAddUserFile: attachData => dispatch(addUserFile(attachData)),
  onDeleteUserFile: id => dispatch(deleteUserFile(id)),
  onResetUSERFlag: searchValue => dispatch(resetUSERFlag(searchValue)),
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(UserFiles))
